<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useDashboardStats } from '@/composables/useDashboardStats';
import type { IManageOrganisation } from '@/types/manageOrgCommerce.types';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const { orgs: allOrgs, isPending, isError } = useDashboardStats();
const { t } = useI18n({ useScope: 'global' });

type Filter = 'all' | 'active' | 'inactive';
const activeFilter = ref<Filter>('all');

function statusClass(org: IManageOrganisation): 'active' | 'inactive' {
  return org.isActive ? 'active' : 'inactive';
}
function statusLabel(org: IManageOrganisation): string {
  return org.isActive
    ? t('common.active').toUpperCase()
    : t('common.inactive').toUpperCase();
}

const filteredOrgs = computed(() => {
  const sorted = [...allOrgs.value].sort(
    (a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0),
  );
  const base =
    activeFilter.value === 'all'
      ? sorted
      : sorted.filter((o) => statusClass(o) === activeFilter.value);
  return base.slice(0, 6);
});

function downloadCSV() {
  const rows = [
    ['Société', 'Statut'],
    ...filteredOrgs.value.map((o) => [o.name, statusLabel(o)]),
  ];
  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `clients.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
</script>

<template>
  <section class="clients-section">
    <!-- Header -->
    <div class="clients-section__head">
      <div>
        <h3 class="clients-section__title">
          {{ t('dashboard.clients.title') }}
        </h3>
        <p v-if="!isPending && !isError" class="clients-section__sub">
          {{ t('dashboard.clients.total', { n: allOrgs.length }) }}
          &nbsp;·&nbsp;
          <span class="clients-section__stat clients-section__stat--active">
            {{ allOrgs.filter((o) => o.isActive).length }}
            {{ t('dashboard.clients.active') }}
          </span>
          &nbsp;·&nbsp;
          <span class="clients-section__stat clients-section__stat--inactive">
            {{ allOrgs.filter((o) => !o.isActive).length }}
            {{ t('dashboard.clients.inactive') }}
          </span>
        </p>
      </div>
      <div class="clients-section__actions">
        <button
          class="clients-section__action-btn"
          :class="{
            'clients-section__action-btn--active': activeFilter !== 'all',
          }"
          @click="activeFilter = activeFilter === 'active' ? 'all' : 'active'"
        >
          <i class="pi pi-filter" />
          {{ t('dashboard.clients.filter') }}
        </button>
        <button class="clients-section__action-btn" @click="downloadCSV">
          <i class="pi pi-download" />
          {{ t('dashboard.clients.export') }}
        </button>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="isPending" class="clients-section__skeletons">
      <div v-for="i in 4" :key="i" class="clients-section__skeleton" />
    </div>

    <!-- Erreur -->
    <div v-else-if="isError" class="clients-section__state">
      <i
        class="pi pi-exclamation-circle"
        style="font-size: 2rem; color: #ef4444; opacity: 0.6"
      />
      <p>{{ t('dashboard.clients.loadError') }}</p>
    </div>

    <!-- Table -->
    <div
      v-else-if="filteredOrgs.length > 0"
      class="clients-section__table-wrap"
    >
      <table class="ct">
        <thead>
          <tr>
            <th class="ct__th ct__th--pl">
              {{ t('dashboard.clients.colName') }}
            </th>
            <th class="ct__th">{{ t('dashboard.clients.colResponsable') }}</th>
            <th class="ct__th">{{ t('dashboard.clients.colStatus') }}</th>
            <th class="ct__th">{{ t('dashboard.clients.colCA') }}</th>
            <th class="ct__th ct__th--pr ct__th--right">
              {{ t('dashboard.clients.colAction') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="o in filteredOrgs"
            :key="o.id"
            class="ct__row"
            @click="router.push(`/clients/${o.id}`)"
          >
            <td class="ct__td ct__td--pl">
              <div class="ct__name-cell">
                <div class="ct__avatar">
                  <img
                    v-if="o.logoUrl"
                    :src="o.logoUrl"
                    :alt="o.name"
                    class="ct__avatar-img"
                  />
                  <span v-else>{{
                    o.name
                      .trim()
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((w: string) => w[0])
                      .join('')
                      .toUpperCase()
                  }}</span>
                </div>
                <span class="ct__name">{{ o.name }}</span>
              </div>
            </td>
            <td class="ct__td ct__td--muted">
              {{
                o.contacts?.find((c) => c.isPrimary)?.firstName
                  ? `${o.contacts.find((c) => c.isPrimary)?.firstName} ${o.contacts.find((c) => c.isPrimary)?.lastName ?? ''}`.trim()
                  : '—'
              }}
            </td>
            <td class="ct__td">
              <span class="ct__status" :class="`ct__status--${statusClass(o)}`">
                <span class="ct__status-dot" />
                {{ statusLabel(o) }}
              </span>
            </td>
            <td class="ct__td ct__td--amount">
              {{ o.isActive ? '—' : '0,00€' }}
            </td>
            <td class="ct__td ct__td--pr ct__td--right">
              <button
                class="ct__manage-btn"
                @click.stop="router.push(`/clients/${o.id}`)"
              >
                {{ t('dashboard.clients.manage') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Vide -->
    <div v-else class="clients-section__state">
      <i class="pi pi-users" style="font-size: 2rem; opacity: 0.2" />
      <p>{{ t('dashboard.clients.noClients') }}</p>
    </div>

    <!-- Footer : Tout voir -->
    <div
      v-if="!isPending && !isError && allOrgs.length > 6"
      class="clients-section__footer"
    >
      <button
        class="clients-section__view-all"
        @click="router.push('/clients')"
      >
        <span>{{ t('dashboard.clients.seeAllN', { n: allOrgs.length }) }}</span>
        <i class="pi pi-arrow-right" />
      </button>
    </div>
  </section>
</template>

<style scoped>
.clients-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 3rem;
  padding: 2.5rem;
  box-sizing: border-box;
}

.clients-section__head {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2.5rem;
}
@media (min-width: 640px) {
  .clients-section__head {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
  }
}

.clients-section__title {
  margin: 0 0 0.25rem;
  font-size: 1.5rem;
  font-weight: 800;
  color: #0b1320;
}
.clients-section__sub {
  margin: 0;
  font-size: 0.875rem;
  color: rgba(11, 19, 32, 0.4);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.clients-section__stat {
  font-weight: 600;
}
.clients-section__stat--active {
  color: #16a34a;
}
.clients-section__stat--inactive {
  color: rgba(11, 19, 32, 0.5);
}

.clients-section__actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}
.clients-section__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(11, 19, 32, 0.05);
  border: none;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  color: rgba(11, 19, 32, 0.6);
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.clients-section__action-btn:hover {
  background: rgba(11, 19, 32, 0.1);
}
.clients-section__action-btn--active {
  background: rgba(255, 214, 10, 0.15);
  color: #0b1320;
}

/* Skeletons */
.clients-section__skeletons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.clients-section__skeleton {
  height: 4rem;
  border-radius: 0.75rem;
  background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* State */
.clients-section__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 3rem;
  color: rgba(11, 19, 32, 0.35);
  text-align: center;
  font-size: 0.875rem;
}
.clients-section__state p {
  margin: 0;
}

/* Table */
.clients-section__table-wrap {
  overflow-x: auto;
}
.ct {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}
.ct__th {
  padding-bottom: 1.25rem;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: rgba(11, 19, 32, 0.3);
  border-bottom: 1px solid rgba(11, 19, 32, 0.05);
}
.ct__th--pl {
  padding-left: 0.5rem;
}
.ct__th--pr {
  padding-right: 0.5rem;
}
.ct__th--right {
  text-align: right;
}

.ct__row {
  cursor: pointer;
  border-bottom: 1px solid rgba(11, 19, 32, 0.05);
  transition: background 0.12s;
}
.ct__row:last-child {
  border-bottom: none;
}
.ct__row:hover {
  background: rgba(11, 19, 32, 0.01);
}

.ct__td {
  padding: 1.5rem 0.75rem 1.5rem 0;
  vertical-align: middle;
  font-size: 0.875rem;
}
.ct__td--pl {
  padding-left: 0.5rem;
}
.ct__td--pr {
  padding-right: 0.5rem;
}
.ct__td--right {
  text-align: right;
}
.ct__td--muted {
  color: rgba(11, 19, 32, 0.6);
}
.ct__td--amount {
  font-weight: 700;
  color: #0b1320;
}

.ct__name-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.ct__avatar {
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 0.75rem;
  background: rgba(11, 19, 32, 0.05);
  color: #0b1320;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.ct__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ct__name {
  font-weight: 800;
  font-size: 0.875rem;
  color: #0b1320;
}

.ct__status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
}
.ct__status--active {
  background: rgba(22, 163, 74, 0.07);
  color: #16a34a;
}
.ct__status--inactive {
  background: rgba(11, 19, 32, 0.05);
  color: rgba(11, 19, 32, 0.4);
}

.ct__status-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  flex-shrink: 0;
}
.ct__status--active .ct__status-dot {
  background: #22c55e;
}
.ct__status--inactive .ct__status-dot {
  background: rgba(11, 19, 32, 0.2);
}

.ct__manage-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.25rem;
  background: #0b1320;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}
.ct__manage-btn:hover {
  background: rgba(11, 19, 32, 0.85);
}

/* Footer "Tout voir" */
.clients-section__footer {
  display: flex;
  justify-content: center;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(11, 19, 32, 0.05);
  margin-top: 0.5rem;
}
.clients-section__view-all {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.75rem;
  border-radius: 9999px;
  border: 1.5px solid rgba(11, 19, 32, 0.12);
  background: none;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  color: #0b1320;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    box-shadow 0.15s;
}
.clients-section__view-all:hover {
  background: rgba(11, 19, 32, 0.04);
  border-color: rgba(11, 19, 32, 0.25);
  box-shadow: 0 2px 12px rgba(11, 19, 32, 0.08);
}
</style>

<style>
[data-theme='dark'] .clients-section {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .clients-section__title {
  color: #f1f5f9;
}
[data-theme='dark'] .clients-section__sub {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .clients-section__action-btn {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .clients-section__action-btn:hover {
  background: rgba(255, 255, 255, 0.12);
}
[data-theme='dark'] .ct__th {
  color: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .ct__row {
  border-color: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .ct__row:hover {
  background: rgba(255, 255, 255, 0.03);
}
[data-theme='dark'] .ct__name {
  color: #f1f5f9;
}
[data-theme='dark'] .ct__avatar {
  background: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}
[data-theme='dark'] .ct__td--muted {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ct__td--amount {
  color: #f1f5f9;
}
[data-theme='dark'] .ct__manage-btn {
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ct__manage-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .ct__status--inactive {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .clients-section__skeleton {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.07) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
}
[data-theme='dark'] .clients-section__footer {
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .clients-section__view-all {
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .clients-section__view-all:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2);
}
</style>
